import {
  EntityLocationDTO,
  MapPinDTO,
  MultiTenantLocation,
  PestPacMultiTenantLocation,
  isPestPacMember,
} from './typings'
import {
  excludeClosedLinkedSalesCenterPins,
  excludeKnockStatusPinsCoveredByPestPac,
  excludeLinkedPestPacPins,
  mergeColocatedSalesCenterAndPestPacPins,
} from './api'
import { areColocatedCoordinates } from './colocation'
import { pestPacPinId } from '@user/utils/mapPinId'
import { MapPinTypeVariant } from '@user/components/HereMap/MapPin/types'

// Matched case-insensitively (lowercase) against `entityType?.toLowerCase()`.
export const PESTPAC_ENTITY_TYPE = MapPinTypeVariant.PestPac

export const KNOCK_STATUS_FIELDS = [
  'knockStatusHistoryId',
  'knockStatusLocationId',
  'knockStatusSettingName',
  'knockStatusSettingColor',
  'knockStatusSettingIcon',
  'knockStatusReason',
  'knockStatusCreatedTime',
  'knockStatusCreatedByName',
  'knockStatusCount',
] as const satisfies readonly (keyof MultiTenantLocation & keyof MapPinDTO)[]

type KnockStatusSource = MultiTenantLocation | MapPinDTO

export function withRefreshedPestPacKnockStatus(
  members: MultiTenantLocation[],
  pins: MapPinDTO[],
): MultiTenantLocation[] {
  const byLocationId = new Map<string, KnockStatusSource[]>()
  const index = (
    locationId: string | null | undefined,
    source: KnockStatusSource,
  ) => {
    if (!locationId) return
    const bucket = byLocationId.get(locationId)
    if (bucket) bucket.push(source)
    else byLocationId.set(locationId, [source])
  }

  for (const pin of pins) {
    index(pin.pestPacLocationId, pin)
    for (const member of pin.entities ?? []) {
      index(member.pestPacLocationId, member)
    }
  }

  let changed = false
  const refreshed = members.map((member) => {
    if (!isPestPacMember(member)) return member

    const candidates = byLocationId.get(member.pestPacLocationId) ?? []
    const colocated = candidates.filter((candidate) =>
      areColocatedCoordinates(
        candidate.latitude,
        candidate.longitude,
        member.latitude,
        member.longitude,
      ),
    )
    const hasCoordinates = member.latitude != null && member.longitude != null
    const fresh =
      colocated[0] ??
      (!hasCoordinates && candidates.length === 1 ? candidates[0] : undefined)
    if (!fresh) return member

    const updated: MultiTenantLocation = { ...member }
    let memberChanged = false

    for (const field of KNOCK_STATUS_FIELDS) {
      const value = fresh[field] ?? null
      if (value !== (member[field] ?? null)) memberChanged = true
      Object.assign(updated, { [field]: value })
    }

    if (!memberChanged) return member

    changed = true
    return updated
  })

  return changed ? refreshed : members
}

function pestPacMemberFields(member: PestPacMultiTenantLocation) {
  return {
    pinId: pestPacPinId(
      member.pestPacLocationId,
      member.pestPacDetails?.locationCode,
    ),
    count: 1,
    multitenant: false,
    entities: [] as MultiTenantLocation[],
    pestPacLocationId: member.pestPacLocationId,
    pestPacDetails: member.pestPacDetails,
    street1: member.street1 ?? undefined,
    street2: member.street2 ?? undefined,
    city: member.city ?? undefined,
    stateAbbreviation: member.stateAbbreviation ?? undefined,
    postalCode: member.postalCode ?? undefined,
    country: member.country ?? undefined,
    knockStatusHistoryId: member.knockStatusHistoryId,
    knockStatusLocationId: member.knockStatusLocationId,
    knockStatusSettingName: member.knockStatusSettingName ?? undefined,
    knockStatusSettingColor: member.knockStatusSettingColor,
    knockStatusSettingIcon: member.knockStatusSettingIcon,
    knockStatusReason: member.knockStatusReason ?? undefined,
    knockStatusCreatedTime: member.knockStatusCreatedTime,
    knockStatusCreatedByName: member.knockStatusCreatedByName,
    knockStatusCount: member.knockStatusCount,
  }
}

export function pestPacMemberToPin(
  member: PestPacMultiTenantLocation,
): EntityLocationDTO {
  return {
    entityId: 0,
    entityType: member.entityType,
    latitude: member.latitude ?? 0,
    longitude: member.longitude ?? 0,
    formattedAddress: member.formattedAddress ?? undefined,
    ...pestPacMemberFields(member),
  }
}

export function pestPacMemberAsSinglePin(
  member: PestPacMultiTenantLocation,
  group: MapPinDTO,
): MapPinDTO {
  return {
    ...group,
    ...pestPacMemberFields(member),
    formattedAddress: member.formattedAddress ?? group.formattedAddress,
    latitude: member.latitude ?? group.latitude,
    longitude: member.longitude ?? group.longitude,
  }
}

export function pestPacPinAsMember(
  pin: MapPinDTO,
  pestPacLocationId: string,
): PestPacMultiTenantLocation {
  return {
    entityType: pin.entityType,
    pestPacLocationId,
    pestPacDetails: pin.pestPacDetails,
    formattedAddress: pin.formattedAddress,
    street1: pin.street1,
    street2: pin.street2,
    city: pin.city,
    stateAbbreviation: pin.stateAbbreviation,
    postalCode: pin.postalCode,
    country: pin.country,
    latitude: pin.latitude,
    longitude: pin.longitude,
    knockStatusHistoryId: pin.knockStatusHistoryId,
    knockStatusLocationId: pin.knockStatusLocationId,
    knockStatusSettingName: pin.knockStatusSettingName,
    knockStatusSettingColor: pin.knockStatusSettingColor,
    knockStatusSettingIcon: pin.knockStatusSettingIcon,
    knockStatusReason: pin.knockStatusReason,
    knockStatusCreatedTime: pin.knockStatusCreatedTime,
    knockStatusCreatedByName: pin.knockStatusCreatedByName,
    knockStatusCount: pin.knockStatusCount,
  }
}

// A PestPac tile pin drops its knock-status fields once the badge is cleared, yet the detail
// endpoint still resolves the backing location (kept history). Merge those fields back onto the
// pin so "View History" survives a clear + reopen, not just while the panel stays open (SH-661).
// `detail` is the untyped PestPac detail response (see fetchPestPacLocationDetail).
export function withPestPacKnockStatus(
  pin: EntityLocationDTO | null,
  detail: any,
): EntityLocationDTO {
  const ks = detail?.knockStatus
  return {
    ...pin,
    knockStatusLocationId: ks?.knockStatusLocationId ?? null,
    knockStatusCount: ks?.knockStatusCount ?? null,
    knockStatusSettingName: ks?.knockStatusName ?? undefined,
    knockStatusReason: ks?.reason ?? undefined,
    knockStatusCreatedTime: ks?.createdTime ?? null,
    knockStatusCreatedByName: ks?.createdByName ?? null,
  } as EntityLocationDTO
}

/**
 * Carries forward prior PestPac pins that are missing from a new search
 * fulfilled payload. PestPac tiles resolve independently of (and often
 * slower than) the Sales Center search fetch, so without this the pins
 * blink out for a frame whenever a search re-fetches Sales Center locations.
 * Pins already present in the new payload (by pinId) are not duplicated —
 * the new payload's version wins.
 *
 * `visibleNewLocations` (defaults to `newLocations`) drives the "is this carried PestPac
 * pin's account already represented by an SC pin" check only. overrideFilters makes
 * `newLocations` include every entity type, even ones the active filter hides — a lead
 * hidden by the entity-type filter but linked to this PestPac account would otherwise
 * suppress its carried-forward pin here (via excludeLinkedPestPacPins) on the assumption
 * the lead itself renders, then get filtered back out downstream, leaving neither pin
 * visible (SH-2100). Pass the caller's post-refilter location set to avoid that.
 */
export function mergePestPacPinsForward(
  newLocations: EntityLocationDTO[],
  priorLocations: EntityLocationDTO[],
  visibleNewLocations: EntityLocationDTO[] = newLocations,
): EntityLocationDTO[] {
  const existingPinIds = new Set(newLocations.map((l) => l.pinId))
  const carriedPestPacPins: EntityLocationDTO[] = []
  const carriedPinIds = new Set<string>()
  const memberPinIds = new Set<string>()
  const carry = (pin: EntityLocationDTO, fromMultiRecordPin = false) => {
    if (existingPinIds.has(pin.pinId) || carriedPinIds.has(pin.pinId)) return
    carriedPinIds.add(pin.pinId)
    if (fromMultiRecordPin) memberPinIds.add(pin.pinId)
    carriedPestPacPins.push(pin)
  }

  for (const prior of priorLocations) {
    if (prior.entityType?.toLowerCase() === PESTPAC_ENTITY_TYPE) {
      carry(prior)
      continue
    }
    // A PestPac record sharing an address with a Sales Center record was folded into that
    // record's multi-record pin, so it is only present as a member there. Without carrying it,
    // the Sales Center record rendered alone for the length of the PestPac fetch and then
    // flipped back to a multi-record pin — a flicker on every pan/zoom during a search (SH-2100).
    if (prior.multitenant) {
      for (const member of prior.entities ?? []) {
        if (isPestPacMember(member)) carry(pestPacMemberToPin(member), true)
      }
    }
  }
  if (carriedPestPacPins.length === 0) return newLocations

  // Run the carried pins through the same reconcilers the second (PestPac)
  // dispatch uses in useApi.ts, so a carried-forward pin can't duplicate a
  // closed-linked SC pin or a knock-status pin at the same rooftop.
  //
  // This reconciles the fresh SC payload against the *carried* (potentially
  // stale) PestPac pins, not the not-yet-arrived fresh ones — if a carried
  // pin's backing PestPac account is gone by the time the real fetch
  // resolves, a still-valid closed-linked SC or knock-status pin can be
  // wrongly excluded for this one render. useApi.ts's second dispatch
  // recomputes the same exclusions against the fresh PestPac data and
  // corrects it on the next render. Accepted as a one-frame trade-off
  // against the alternative — a persistent duplicate pin (SH-1341 review).
  const scBase = excludeKnockStatusPinsCoveredByPestPac(
    excludeClosedLinkedSalesCenterPins(newLocations, carriedPestPacPins),
    carriedPestPacPins,
  )
  const survivors = excludeLinkedPestPacPins(
    visibleNewLocations,
    carriedPestPacPins,
  )
  const survivingMembers = survivors.filter((pin) =>
    memberPinIds.has(pin.pinId),
  )
  if (survivingMembers.length === 0) return [...scBase, ...survivors]

  // A carried member was folded into a Sales Center record's multi-record pin. Re-fold it with
  // that record when it will render, as useApi.ts does for the real fetch, so the pending render
  // matches the final one. Hidden-by-filter pins are left alone — the caller re-filters them.
  const visiblePinIds = new Set(visibleNewLocations.map((pin) => pin.pinId))
  return [
    ...scBase.filter((pin) => !visiblePinIds.has(pin.pinId)),
    ...mergeColocatedSalesCenterAndPestPacPins(
      scBase.filter((pin) => visiblePinIds.has(pin.pinId)),
      survivingMembers,
    ),
    ...survivors.filter((pin) => !memberPinIds.has(pin.pinId)),
  ]
}
